import { CcfBox, CcfTypography, useTranslator } from '@nice-ccf/ui-controls';
import styles from './email-interaction-flow.module.scss';
import { Avatar, Divider } from '@mui/material';
import React, { useMemo } from 'react';
import { ArrowDropDown } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { activeContactSelector } from 'libs/react/ui-components/src/lib/ccf-inbox/ccf-inbox.slice';

/* eslint-disable-next-line */
export interface EmailInteractionFlowProps {
  interaction?: any;
}

/**
 * EmailInteractionFlow - rused to display Email Interaction Flow in Interaction Tab Details View
 * @param props - EmailInteractionFlowProps
 * @returns - appspace
 * @example - `<EmailInteractionFlow />`
 */
export function EmailInteractionFlow(props: EmailInteractionFlowProps) {
  const { interaction } = props;
  const [translate] = useTranslator();
  const activeContact = useSelector(activeContactSelector);
  const customerName = `${activeContact?.customer.firstName} ${activeContact?.customer.lastName}`;

  function formatDate(dateString: string, timeOffset: number = 0): string {
    const date = new Date(dateString);
    const now = new Date();

    // Apply the time offset
    date.setMinutes(date.getMinutes() + timeOffset);

    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    const dayOfWeek = daysOfWeek[date.getUTCDay()];
    const month = months[date.getUTCMonth()];
    const day = date.getUTCDate();
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

    const formattedDate = `${dayOfWeek}, ${month} ${day} ${formattedHours}:${formattedMinutes} ${ampm}`;

    const timeDifference = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
    );
    const relativeTime =
      timeDifference === 0 ? 'today' : `${timeDifference} days ago`;

    return `${formattedDate} (${relativeTime})`;
  }

  const interactionDate = new Date(interaction.date);

  const EmailThreadData = [
    {
      emailFrom: customerName,
      emailTo: 'customer.service@delta.com',
      date: formatDate(interactionDate.toISOString(), 30),
      emailContent:
        'My claim number is 123456. The dent is on the side, and the zipper is completely off track. It looks like it was pretty rough handling.',
    },
    {
      emailFrom: 'Customer Service',
      emailTo: activeContact?.customer.emailId,
      date: formatDate(interactionDate.toISOString(), 20),
      emailContent:
        'Thank you for the details. Could you provide me with your baggage claim number and a description of the damage?',
    },
    {
      emailFrom: customerName,
      emailTo: 'customer.service@delta.com',
      date: formatDate(interactionDate.toISOString(), 15),
      emailContent:
        'My suitcase has a big dent and the zipper is broken. It happened on flight 789 from New York to Los Angeles.',
    },
    {
      emailFrom: 'Customer Service',
      emailTo: activeContact?.customer.emailId,
      date: formatDate(interactionDate.toISOString(), 5),
      emailContent:
        'I’m really sorry to hear that. Can you give me a bit more detail about the damage and your flight information?',
    },
    {
      emailFrom: customerName,
      emailTo: 'customer.service@delta.com',
      date: formatDate(interactionDate.toISOString(), 0),
      emailContent:
        'Hi there, I’m writing about a problem with my luggage. It arrived damaged after my recent flight.',
    },
  ];

  const stringAvatar = useMemo(
    () => (name: string) => {
      const [firstName, lastName] = name.split(' ');
      return {
        children: `${firstName[0]}${lastName ? lastName[0] : ''}`,
      };
    },
    []
  );

  return (
    <CcfBox className={styles.emailInteractionContainer}>
      {EmailThreadData.map((email, index) => (
        <React.Fragment key={index}>
          <CcfBox className={styles.emailContainer}>
            <CcfBox className={styles.emailHeader}>
              <Avatar
                alt={email.emailFrom}
                {...stringAvatar(email.emailFrom)}
                className={styles.avatar}
              />
              <CcfTypography className={styles.title}>
                {email.emailFrom}
              </CcfTypography>
            </CcfBox>
            <CcfBox className={styles.emailSubHeader}>
              <CcfTypography className={styles.subHeaderTxt}>
                To: {email.emailTo} <ArrowDropDown />
              </CcfTypography>
              <CcfTypography className={styles.subHeaderTxt}>
                {email.date}
              </CcfTypography>
            </CcfBox>
            <CcfBox className={styles.emailContent}>
              <CcfTypography className={styles.emailContentTxt}>
                {email.emailContent}
              </CcfTypography>
              <br />
              <CcfTypography className={styles.emailContentTxt}>
                Thanks, {email.emailFrom}
              </CcfTypography>
            </CcfBox>
          </CcfBox>
          {index !== EmailThreadData.length - 1 && (
            <Divider className={styles.customDivider} />
          )}
        </React.Fragment>
      ))}
      <CcfBox className={styles.horizontalLineWithText}>
        <span>
          {translate('email')} #{interaction.interactionId}{' '}
          {translate('closed')}
        </span>
      </CcfBox>
    </CcfBox>
  );
}

export default EmailInteractionFlow;
